/* enum.control_adapter.js */

const $ = require("jquery");
const _ = require("lodash");
const underscoreString = require("underscore.string");
_.mixin(underscoreString.exports());
const EventEmitter = require("events").EventEmitter;
const util = require("util");
const q = require("q");
const StyleUtil = require("../StyleUtil");

/**
 * @event EnumControlAdapter#ValueChanged
 * @type {Object}
 * @property oldValue {string} The previous value
 * @property newValue {string} The new value
 */

/**
 * @class EnumControlAdapter
 * @classdesc renders the Select Options Jtype.
 * @implements AngularControlAdapter
 *
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires util
 * @requires EventEmitter
 *
 * @fires EnumControlAdapter#ValueChanged
 */
module.exports = function EnumAdapter(logger, containingElem, formCtrlDef) {
    var loadDeferred = q.defer();
    var self = this;
    //call Event Emitter Constructor
    EventEmitter.call(this);
    var _scope;
    var _oldValue;
    this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);
    this._required = false;
    var _oldInvalidStatus = false;
    var _validationMessages = [];

    /********************************************************************************/
    /*** START Type Definitions                                                   ***/
    /********************************************************************************/

    /********************************************************************************/
    /*** END Type Definitions                                                     ***/
    /*** START Private API                                                        ***/
    /********************************************************************************/

    /**
     * Fire the Value Changed Event
     * @private
     */
    function _fireValueChanged() {
        //Get the new Value
        var newVal = self.getValue();
        self.emit("ValueChanged", {
            name: formCtrlDef.name,
            containingElem: containingElem,
            controlAdapter: self,
            formControlDefinition: formCtrlDef,
            oldValue: _oldValue,
            newValue: newVal,
        });
        _oldValue = newVal;
    }

    function _updateValidityStatus() {
        let newInvalidStatus = self.isInvalid();
        if (newInvalidStatus) {
            _validationMessages = ["Please select a value from the drop down."];
            _scope.Enum.selection.api.setInvalid(_validationMessages[0]);
            containingElem.addClass("invalid");
        } else {
            _validationMessages = [];
            _scope.Enum.selection.api.setValid();
            containingElem.removeClass("invalid");
        }
        if (newInvalidStatus !== _oldInvalidStatus) {
            self.emit("Invalid", {
                name: formCtrlDef.name,
                containingElem: containingElem,
                controlAdapter: self,
                formControlDefinition: formCtrlDef,
                oldValue: _oldInvalidStatus,
                newValue: newInvalidStatus,
            });
            _oldInvalidStatus = newInvalidStatus;
            setTimeout(function () {
                _scope.$digest();
            });
        }
    }

    /********************************************************************************/
    /*** END Private API                                                          ***/
    /*** START Public API                                                         ***/
    /********************************************************************************/
    //The name of the control
    this.name = formCtrlDef.name;

    /**
     * @returns the data associated with this control.
     */
    this.getValue = function () {
        var val = null;
        if (_scope.Enum.selection.api.getValue) {
            val = _scope.Enum.selection.api.getValue();
            if (_.isUndefined(val)) {
                val = null;
            }
        }
        return val;
    };

    /**
     * Set this controls current value.
     * @param value {string} The value to set this control to.
     * @returns the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    this.setValue = function (value) {
        if (_scope.Enum.selection.api.setValue) {
            _scope.Enum.selection.api.setValue(value);
        }
        return self.getValue();
    };

    this.getMultiEditActive = function () {
        return this._multiEditActive;
    };

    this.setMultiEditActive = function (flag) {
        if (!this._readOnlyField) {
            this.setReadOnly(!flag);
            let oldValue = this._multiEditActive;
            this._multiEditActive = flag;
            if (oldValue !== this._multiEditActive) {
                this.emit("MultiEditActive", {
                    oldValue: oldValue,
                    newValue: this._multiEditActive,
                });
            }
        }
    };

    this.isInvalid = function () {
        if (this._readOnly) {
            return false;
        } else {
            return this._required ? this.getValue() === null : false;
        }
    };

    this.getValidationMessages = function () {
        return _validationMessages.slice(0);
    };

    /**
     * Tear down this control
     */
    this.tearDown = function () {
        //Do Nothing the dom is cleaned up, the scope is destroyed for us and any listeners
        //on self are unregistered automatically
    };

    /********************************************************************************/
    /*** END Public API                                                           ***/
    /*** Start Angular/Render Work                                                ***/
    /********************************************************************************/

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angularis before this hook is called.
     * @param $scope {$scope} Angular $scope
     * @parma $templateCache {$templateCache}
     */
    this.$init = function ($scope) {
        _scope = $scope;

        var opts = [];
        var useDisplayOpts = false;

        let stylesConfig = _.get(formCtrlDef, "configSettings.Display.styles", {});
        StyleUtil.renderStylesConfig(stylesConfig, containingElem[0]);

        // Apply item layout styles - these styles customize how this boolean element is placed in parent layout
        let itemLayoutCssClass = _.get($scope, "jTypeContext.layout.item.cssClass", "");
        containingElem.addClass(itemLayoutCssClass);

        StyleUtil.appendClassListToElement(
            $scope.jTypeContext.layout.inlineLabels,
            containingElem[0]
        );

        if (_.has(formCtrlDef, "configSettings.Display.enum")) {
            useDisplayOpts = true;
        }

        formCtrlDef.configSettings.Enum.forEach(function (option, idx) {
            var label;
            if (useDisplayOpts) {
                label = formCtrlDef.configSettings.Display.enum[idx] || option;
                // Make sure it's a string
                label = "" + label;
            } else {
                label = option;
                if (!_.isString(option)) {
                    label = new String(option);
                }
            }
            opts.push({label: label, value: option});
        });

        //Check if selection can be removed, or if it is required.
        if (formCtrlDef.validationInfo && formCtrlDef.validationInfo.required) {
            this._required = true;
        }

        let initialValue = formCtrlDef.value;

        if (this._multiEditMode) {
            initialValue = null;
        }
        //Setup Settings Object
        $scope.Enum = {
            selection: {
                api: {},
                settings: {
                    disableAutoSelect: true,
                    allowSingleDeselect: !this._required,
                    sort: false,
                    options: opts,
                    value: initialValue,
                    onUserChange: function (newVal, OldVal) {
                        _fireValueChanged();
                        _updateValidityStatus();
                    },
                },
            },
        };

        //Setup directive
        var enumElem = $(
            '<gl-multi-select class="form_entry" settings="Enum.selection.settings" api="Enum.selection.api"></gl-multi-select>'
        );
        containingElem.append(enumElem);

        //Notify the world this adapter is ready to be compiled/linked and values retrieved
        self.emit("InitializationComplete", {
            initializedValue: self.getValue(),
        });
        return loadDeferred.promise;
    };
    this.$init.$inject = ["$scope"];

    /**
     * A Hook for when this control has been compiled/Linked.
     * @param $scope {$scope} The Angular Scope variable
     *
     * if $compiledAndLinked.$inject is specified those injection parameters will be used as the arguments
     */
    this.$compiledAndLinked = function ($scope, $timeout) {
        // If the default value is a number then we have to update the
        // default value here because the initial value did not take.
        if (_.isNumber(formCtrlDef.value)) {
            $scope.Enum.selection.api.setValue(formCtrlDef.value);
        }
        if (_.get(formCtrlDef, "configSettings.Display.readOnly")) {
            this._readOnlyField = true;
            this.setReadOnly(true);
        }

        // If this is multiEditMode, set the field as read only by default
        if (this._multiEditMode) {
            this.setReadOnly(true);
        }

        if (typeof formCtrlDef.configData !== "undefined") {
            //Update used/available based on configData
            self.setValue(formCtrlDef.configData);
            _updateValidityStatus();
            $timeout(function () {
                loadDeferred.resolve();
            });
        } else {
            _updateValidityStatus();
            loadDeferred.resolve();
        }
    };
    this.$compiledAndLinked.$inject = ["$scope", "$timeout"];

    /********************************************************************************/
    /*** END Angular/Render Work                                                  ***/
    /********************************************************************************/
    this.setReadOnly = function (flag) {
        if (flag) {
            _scope.Enum.selection.api.disable();
        } else {
            _scope.Enum.selection.api.enable();
        }
        this._readOnly = flag;
        _updateValidityStatus();
    };
};
//Make this API inherit from EventEmitter
util.inherits(module.exports, EventEmitter);

//Tell the Form renderer that this is an Angular Control.
module.exports.isAngularControl = true;

//Tell the Form renderer that this adapter supports multi edit mode.
module.exports.isMultiEditSupported = true;
